import React, { useMemo } from 'react';
import CustomModal from '../../baseComponents/gui/customModal/CustomModal';
import { useModal } from '../../../hooks/useModal';
import CustomButton from '../../features/customButton/ui/main/CustomButton';
import { safeHTML } from '../../../utils/safeHTML';
import { image } from '../../../utils/data/baseUrl';
import classNames from 'classnames';
import { procedureGameSelectionContent, procedurePrizeContent } from '../../../constants/copyright-modals';
import useStateControls from '../../../hooks/useStateControls';
import { useTranslation } from '../../../translations';
import { useLanguageControls } from '../../../hooks/useLanguageControls';

const ProcedureGameResultModal = React.forwardRef(function ProcedureGameResult(
  { title, text, buttons, items, score, userPrize, callbacks: appCallbacks },
  ref,
) {
  const { addModal, closeModal } = useModal();
  const { setState } = useStateControls();
  const close = () => closeModal({ type: 'all' });
  const { t } = useTranslation();
  const { isArabic } = useLanguageControls();

  const answersData = useMemo(() => {
    const truth = items.filter((el) => el.isRight).length;
    return { truth: truth, wrong: items.length - truth };
  }, [items]);

  const callbacks = {
    sets: () => {
      addModal({
        type: 'selectionModal',
        props: { ...procedureGameSelectionContent, filter: 'procedure', callbacks: appCallbacks },
      });
    },
    home: () => {
      close();

      setState('main');

      if (!userPrize) return;

      const { promocode, description, image, link, title, type } = userPrize;
      addModal({
        type: 'adminCustomModal',
        props: {
          image,
          promocodeData: { promocode, description, title, type },
          callbacks,
          ...procedurePrizeContent,
          buttons: procedurePrizeContent.buttons.map(
            (buttonData) =>
              ({
                toShop: { ...buttonData, link },
                close: buttonData,
              }[buttonData.action]),
          ),
        },
      });
    },
  };

  return (
    <CustomModal animation={['fade', 'scale', 'scale-bounce']}>
      <div className={'procedure-game-result'} ref={ref}>
        <div className={'procedure-game-result__bg'} />
        <div className={'procedure-game-result__content'}>
          <h2 className={'procedure-game-result__title'}>{safeHTML(t(title))}</h2>
          {answersData && (
            <div className='procedure-game-result__info'>
              <div className='procedure-game-result__info-item'>
                <img
                  className='procedure-game-result__info-item-image'
                  src={image('procedureGame/right.svg')}
                  alt={''}
                />
                <span className='procedure-game-result__info-item-text'>
                  {answersData.truth} {t('other.correct')}
                </span>
              </div>
              <div className='procedure-game-result__info-item'>
                <img
                  className='procedure-game-result__info-item-image'
                  src={image('procedureGame/wrong.svg')}
                  alt={''}
                />
                <span className='procedure-game-result__info-item-text'>
                  {answersData.wrong} {t('other.wrong')}
                </span>
              </div>
            </div>
          )}
          <div className={'procedure-game-result__items'}>
            {items.map(({ isRight, img }, id) => (
              <div
                key={`icon-${id}`}
                className={classNames('procedure-game-result__item', {
                  'procedure-game-result__item_right': isRight,
                })}
              >
                <img src={img} alt={''} />
              </div>
            ))}
          </div>
          <div className='procedure-game-result__hp'>
            <div className='procedure-game-result__hp-title'>{safeHTML(isArabic ? `+XP${score}` : `+${score}XP`)}</div>
            <div className='procedure-game-result__hp-text'>{safeHTML(t('other.toRating'))}</div>
          </div>
          <p className='procedure-game-result__text'>{safeHTML(text)}</p>
          {buttons.map(({ attr, action }, id) => (
            <CustomButton key={`b-${id}`} {...attr} onClick={callbacks[action]} />
          ))}
        </div>
      </div>
    </CustomModal>
  );
});

export default ProcedureGameResultModal;
ProcedureGameResultModal.propTypes = {};
